import React, { useRef, useState, useCallback } from 'react';
import { Scheduler } from "react-big-schedule";
import "react-big-schedule/dist/css/style.css";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { openModalCreateEditWorkOrder } from "../../.globals/redux/actions";
import useScheduler from './hooks/useScheduler';
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, SearchInput, Tooltip } from "@gpstrackit/gtc-react-controls";
import { ConfigProvider } from 'antd';
import { FilterAvatars } from '../../../.common/components/FilterAvatars';
import { savePreference } from 'Redux/actions';
import Preferences from 'Core/containers/Preferences';
import { NotificationContainer } from "Components/Notifications";
import { toggleModalWorkOrdersScheduleAdvanceFilter } from './modules/AdvanceFilter/redux/slice';
import AdvanceFilter from './modules/AdvanceFilter';
import CreateEditWorkOrder from '../trackerTab/modals/createEditWorkOrder';
import moment from 'moment';

// Ant Design theme configuration
const antdTheme = {
  token: {
    colorPrimary: '#04ADEF',
    colorLink: '#04ADEF',
    colorLinkHover: '#04ADEF',
    borderRadius: 4,
  },
  components: {
    Button: {
      colorPrimary: '#04ADEF',
      algorithm: true,
    },
    Radio: {
      colorPrimary: '#04ADEF',
      algorithm: true,
    },
    Calendar: {
      colorPrimary: '#04ADEF',
      algorithm: true,
    }
  }
};

// Import styles
import './index.scss';

const KEY_PREFERENCE = "workOrdersScheduleFiltersView-3ln0rt3";
const INITIAL_STATE = {
  severity: null,
  status: null,
  type: null,
  rangeDate: {
    start: moment().startOf('month'),
    end: moment().endOf('month')
  },
  timeFrame: 'thisMonth',
  tags: []
}


const App = () => {
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const schedulerContainerRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [loadPreference, setLoadPreference] = useState(false);
  const [preferences, setPreferences] = useState({});
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);


  const savePreferences = (preferencesArray) => {
    if (!Array.isArray(preferencesArray)) {
      console.error("Expected an array of preferences");
      return;
    }

    let updatedPreferences = { ...preferences } || {};

    preferencesArray.forEach(({ key, value }) => {
      updatedPreferences[key] = value;
    });

    setPreferences(updatedPreferences);

    dispatch(savePreference({
      entityName: "User",
      entityId: authenticatedId,
      preferenceKey: KEY_PREFERENCE,
      preferenceValue: updatedPreferences
    }));
  };

  const hasFilters = useCallback(() => {
    const excludedKeys = ["rangeDate"];
    const defaultValues = {
      severity: null,
      status: null,
      type: null,
      tags: [],
      timeFrame: "thisMonth"
    };

    return Object.keys(formData).some(key => {
      if (excludedKeys.includes(key)) return false;

      const currentValue = formData[key];
      const defaultValue = defaultValues[key];

      // Special handling for arrays (tags)
      if (Array.isArray(currentValue)) {
        return currentValue.length > 0;
      }

      // Special handling for objects
      if (typeof currentValue === 'object' && currentValue !== null) {
        return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
      }

      // Comparison for primitive values
      return currentValue !== defaultValue;
    });
  }, [formData]);


  const {
    schedulerData,
    prevClick,
    applyChanges,
    nextClick,
    onSelectDate,
    onViewChange,
    eventItemClick,
    newEvent,
    resourceFilter,
    handleFilterChange,
    moveEvent,
    resizeEvent,
    onScrollLeft,
    onScrollRight,
    onScrollTop,
    onScrollBottom,
    toggleExpandFunc,
    updateEventStart,
    updateEventEnd,
    conflictOccurred,
    eventItemPopoverTemplateResolver,
    refreshScheduler
  } = useScheduler([], selectedDrivers, formData, loadPreference, hasFilters);
  const openModalCreateEdit = useSelector((state) => state.workOrdersCreateEditWorkOrderRedux.open);
  const scheduleWorkOrdersLoading = useSelector(state => state.workOrdersScheduleRedux.scheduleWorkOrdersLoading);

  return (
    <ConfigProvider theme={antdTheme}>
      <div className='scheduler'>
        <section className='scheduler__container' ref={schedulerContainerRef}>
          <Preferences
            preferencesKey={KEY_PREFERENCE}
            // data is the object saved in preferences
            onInitialPreferences={(data) => {
              if (!loadPreference) {
                let updatedFormData = {
                  ...formData,
                  ...['severity', 'status', 'type', 'rangeDate', 'timeFrame', 'tags', 'customers'].reduce((acc, key) => {
                    if (data?.[key]) acc[key] = data[key];
                    return acc;
                  }, {})
                }
                setFormData(updatedFormData);
                setPreferences(data);
                setLoadPreference(true);
              }
            }}
          >
            {/* Driver search and status filter */}
            <section className='scheduler__header'>
              <div className='scheduler__actions'>
                <IconButton
                  tooltip={messages["filters"]}
                  placement="bottom"
                  icon="filter_list"
                  size="small"
                  disabled={true}
                  onClick={() => dispatch(toggleModalWorkOrdersScheduleAdvanceFilter({ open: true }))}
                  className={hasFilters() ? "trackerTab-filters-available" : ""}
                />
                <IconButton
                  tooltip={messages["add"]}
                  placement="bottom"
                  variant="secondary"
                  icon="add_box"
                  size="small"
                  disabled={false}
                  onClick={() => {
                    dispatch(openModalCreateEditWorkOrder())
                  }}
                />
                <div className='scheduler__filter-avatars'>
                  <FilterAvatars
                    selectedDrivers={selectedDrivers}
                    setSelectedDrivers={setSelectedDrivers}
                  />
                </div>
              </div>
              <div className='scheduler__search'>
                <Tooltip title={messages["schedule_workOrder_search_events"]}>
                  <SearchInput
                    placeholder={messages["schedule_workOrder_search_events"]}
                    searchText={resourceFilter}
                    onSearchText={(value) => handleFilterChange(value)}
                  />
                </Tooltip>
              </div>
            </section>
            <DndProvider backend={HTML5Backend}>
              {/* Render the scheduler only when data is available */}
              {schedulerData && (
                <div className={`scheduler__content ${scheduleWorkOrdersLoading ? 'scheduler__content--loading' : ''}`}>
                  <Scheduler
                    schedulerData={schedulerData}
                    prevClick={prevClick}
                    nextClick={nextClick}
                    onSelectDate={onSelectDate}
                    onViewChange={onViewChange}
                    eventItemClick={eventItemClick}
                    newEvent={newEvent}
                    moveEvent={moveEvent}
                    resizeEvent={resizeEvent}
                    onScrollLeft={onScrollLeft}
                    onScrollRight={onScrollRight}
                    onScrollTop={onScrollTop}
                    onScrollBottom={onScrollBottom}
                    toggleExpandFunc={toggleExpandFunc}
                    updateEventStart={updateEventStart}
                    updateEventEnd={updateEventEnd}
                    conflictOccurred={conflictOccurred}
                    parentRef={schedulerContainerRef}
                    eventItemPopoverTemplateResolver={eventItemPopoverTemplateResolver}
                  />
                </div>
              )}
            </DndProvider>
            {openModalCreateEdit && <CreateEditWorkOrder refresh={refreshScheduler} />}
            <NotificationContainer />
          </Preferences>
          <AdvanceFilter savePreferences={savePreferences} formData={formData} setFormData={setFormData} hasFilters={hasFilters} initialState={INITIAL_STATE} applyChanges={applyChanges} />
        </section>
      </div>
    </ConfigProvider>
  );
}

const buildConditions = (formData) => {
  const conditions = [];
  if (formData?.status?.id) conditions.push({ fields: "statusId", terms: [formData.status.id], exact: true });
  if (formData?.severity?.id) conditions.push({ fields: "severity", terms: [formData.severity.id], exact: true });
  if (formData?.type?.id) conditions.push({ fields: "workOrderTypeId", terms: [formData.type.id], exact: true });
  if (formData?.tagIds?.length) conditions.push({ fields: "tagIds", terms: formData.tagIds, exact: true });
  conditions.push({ range: { fields: ["scheduledDate"], GTE: formData.rangeDate.start, LTE: formData.rangeDate.end } });
  return conditions;
};

export default App;
